<template lang="pug">
  .panel-page.body-1
    FormErrorAlert.mx-auto(
      heading="Errors"
      :message="message"
      v-if="message"
      @dismiss="message = ''"
    )
    form.panel.mx-auto(
      @submit.prevent="checkForm()"
      autocomplete="off"
    )
      h1.mb-3 User Registration
      h4.mb-3 I want to begin registration using:
      CustomRadio(modelValue="address",v-model="agencyIdentifier")
        span.body-1.ml-2 My agency's primary address
      br
      CustomRadio(modelValue="ori",v-model="agencyIdentifier")
        span.body-1.ml-2 My criminal justice agency's ORI code
      div.mt-2(v-if="agencyIdentifier === 'address'")
        .row.border-bottom
          .col
            span.h5 AGENCY'S PRIMARY ADDRESS
          .col
            p.text-right.body-1
              span.red *
              |  Indicates a required field.
        .address-form.p-3.mt-3
          .form-group
            label(for='address1')
              | Agency Street Address
              span.red *
            input.form-control(v-model='address1', type='text', @input="address1Touched = true", :class="{'is-invalid': address1Invalid }", name='address1')
            FormError(v-if="address1Invalid", message="Agency street address is required.")
          .form-group
            label(for='address2') Address Line 2
            input#address2.form-control(v-model='address2', type='text', name='address2')
          .form-row
            .col-md.form-group
              label(for='city') City
              input#city.form-control(v-model='city', type='text', name='city')
            .col-md.form-group
              label(for='agency_state') State
              span.red *
              CustomSelect#agency_state(v-model="addressState",:options="states",labelName="name",valueName="abbreviation",@input="addressStateTouched = true",:invalid="addressStateInvalid || addressState === 'OR'")
              FormError(v-if="addressStateInvalid || addressState === 'OR'", :message="addressState === 'OR' ? 'Oregon users may not register.' : 'Agency State is required.'")
            .col-md.form-group
              label(for='zip')
                | Zip
                span.red *
              input.form-control(v-model='zip', type='text', @input="zipTouched = true", :class="{'is-invalid': zipInvalid}", name='zip')
              FormError(v-if="zipInvalid", message="Zip code is required.")
      //- Else, show the ORI form
      div.mt-2(v-if="agencyIdentifier === 'ori'")
        .row.border-bottom
          .col
            h5 AGENCY'S ORI CODE
          .col
            p.text-right.body-1
              span.red *
              |  Indicates a required field.
        .address-form.p-3.mt-3
          .form-row
            .col.form-group
              label(for='ori')
                | ORI
                span.red *
              input.form-control(v-model='ori', type='text', @input="oriTouched = true", :class="{'is-invalid': oriInvalid}", name='ori')
              FormError(v-if="oriInvalid", message="Agency ORI is required.")
          .form-row
            .col.form-group
              label(for='agency_state')
                | Agency State
                span.red *
              CustomSelect#agency_state(
                :options="states"
                labelName="name"
                valueName="abbreviation"
                v-model="oriState"
                @input="oriStateTouched = true"
                :invalid="oriStateInvalid || oriState === 'OR'"
              )
              FormError(v-if="oriStateInvalid || oriState === 'OR'", :message="oriState === 'OR' ? 'Oregon users may not register.' : 'Agency State is required.'")
      button.btn.btn-primary.mt-3 Continue
</template>

<script>
import { globalError } from '@/lib/utils'
import { mapTouched, mapRequired } from '@/lib/validation'
import axios from '@/lib/axios'
import CustomRadio from './CustomRadio'
import CustomSelect from './CustomSelect'
import FormError from './FormError.vue'
import FormErrorAlert from './FormErrorAlert.vue'
import states from '../lib/states'
import stdmsg from '@/lib/stdmsg'
import { loadForm } from '@/lib/formstore'

export default {
  name: 'RegistrationAgencyInfo',
  props: {
    model: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },
  data() {
    return {
      message: '',
      states: states,
      agencies: [],
      agencyName: '',
      agencyState: '',
      agencyId: '',
      agencyIdentifier: 'address',
      address1: '',
      address2: '',
      city: '',
      addressState: '',
      oriState: '',
      zip: '',
      ori: '',
      ...mapTouched(['address1', 'zip', 'ori', 'addressState', 'oriState']),
    }
  },
  computed: {
    usingAgencyAddress: function() {
      return this.agencyIdentifier === 'address'
    },
    ...mapRequired(['address1', 'zip', 'ori', 'addressState', 'oriState']),
  },
  methods: {
    checkForm: function() {
      if (this.agencyIdentifier === 'ori' && (!this.ori || !this.oriState)) {
        this.oriTouched = true
        this.oriStateTouched = true
        this.message = stdmsg.formError
        return
      } else if (
        this.agencyIdentifier === 'address' &&
        (!this.address1 || !this.addressState || !this.zip)
      ) {
        this.address1Touched = true
        this.addressStateTouched = true
        this.zipTouched = true
        this.message = stdmsg.formError
        return
      }
      // prohibit Oregon users from registering
      else if (
        (this.agencyIdentifier === 'address' && this.addressState === 'OR') ||
        (this.agencyIdentifier === 'ori' && this.oriState === 'OR')
      ) {
        this.message = 'Oregon users are prohibited from registering.'
        return
      }
      let params = {}
      if (this.agencyIdentifier === 'ori') {
        params.ori = this.ori
        params.state = this.oriState
      } else {
        params.address1 = this.address1
        params.state = this.addressState
        params.zip = this.zip
        if (this.address2.length > 0) params.address2 = this.address2
        if (this.city.length > 0) params.city = this.city
      }
      axios()
        .get('/api/misc/agencies', { params })
        .then(resp => {
          if (resp.data.length > 0) {
            this.agencies = resp.data
            this.agencyName = this.agencies[0].name
            this.agencyState = this.agencies[0].state
            this.agencyId = this.agencies[0].id
            this.$nextTick(() => this.$emit('nextState'))
          } else {
            throw new Error('No agencies found.')
          }
        })
        .catch(err => {
          globalError(err)
        })
    },
  },
  mounted: loadForm(
    'registration',
    'agencies',
    'agencyName',
    'agencyState',
    'agencyId',
    'address1',
    'address2',
    'city',
    'addressState',
    'oriState',
    'zip',
    'ori',
  ),
  components: {
    FormErrorAlert,
    FormError,
    CustomRadio,
    CustomSelect,
  },
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/bodies.scss';
$width: 835px;

.red {
  color: red;
}

.is-invalid {
  border: 1px solid #dc3545;
}

.border-bottom {
  border-bottom: 1px solid #e6e6e6;
}

.alert {
  width: $width;
}

label {
  @extend .body-1;
}

.gone {
  display: none;
}

.address-form {
  background-color: #f8f8f8;
}
</style>
